import { Component, inject } from '@angular/core';
import { map, merge, Observable, startWith, Subject, tap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { SelectorOption } from 'src/app/interfaces/form-and-field.interface';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import {
  SearchParameters,
  SearchResultResponse,
  SortingOrderType,
  SortParams,
} from 'src/app/interfaces/search.interface';
import { Session } from 'src/app/interfaces/session.interface';
import { SearchResult } from 'src/app/swagger-api';
import { SearchCategories, SearchResultSorting } from '../../search.enum';
import { SearchService } from '../../search.service';

@Component({
  selector: 'app-search',
  templateUrl: './basic-search.component.html',
  styleUrls: ['../shared-search/shared-search.component.scss'],
  providers: [SearchService],
})
export class BasicSearchComponent {
  protected isSearchActive = false;
  protected excludeIds: string[] = [];

  protected sortParams: SortParams = {
    ascending: false,
    orderType: SortingOrderType.SCORE,
  };

  protected searchParams: SearchParameters = {
    itemKind: SearchCategories.ALL_CATEGORIES,
    queryText: '',
  };

  // TODO: resetting state is search service responsibility
  private resetSearchState$: Subject<SearchResult[]> = new Subject<SearchResult[]>();

  protected searchService = inject(SearchService);
  protected sessionService = inject(SessionService);

  protected resolver: PaginationResolver = {
    state: this.searchService.initialSearchState(),
    onPageChanged: (page: number) => {
      this.resolver.state.pagination.currentPage = page;
      this.searchService.getSearchResults({
        state: this.resolver.state,
        applyLocationAttrs: true,
      });
    },
  };

  private searchResult$ = this.searchService.searchResultsChanges.pipe(
    tap((result: SearchResultResponse) => {
      this.isSearchActive = true;
      this.resolver.state.pagination.totalItemsCount = result.total_matches_count;
    }),
    map(({ search_results }: SearchResultResponse) => search_results),
  );

  public readonly results$: Observable<SearchResult[]> = merge(
    this.searchResult$,
    this.resetSearchState$.asObservable(),
  ).pipe(startWith([]));

  public readonly isAuthenticated$: Observable<boolean> = this.sessionService.sessionChanges.pipe(
    map((session: Session) => Boolean(session.token)),
  );

  public readonly isLoading$: Observable<boolean> = this.searchService.isLoading$.pipe(startWith(false));

  public readonly searchOptions$: Observable<SelectorOption[]> = this.searchService.searchOptionsChanged.pipe(
    startWith([]),
  );

  public onSearch(params: SearchParameters): void {
    this.resetSearchState$.next([]);
    this.resolver.state = this.searchService.initialSearchState();
    this.searchParams = params;
    this.setSearchResults(params, this.excludeIds, this.sortParams);
  }

  public setSortCategory(sortCategory: string): void {
    switch (sortCategory) {
      case SearchResultSorting.ASCENDING:
        this.sortParams.ascending = true;
        this.sortParams.orderType = SortingOrderType.NAME;
        break;
      case SearchResultSorting.DESCENDING:
        this.sortParams.ascending = false;
        this.sortParams.orderType = SortingOrderType.NAME;
        break;
      case SearchResultSorting.NEWEST:
        this.sortParams.ascending = false;
        this.sortParams.orderType = SortingOrderType.DATE;
        break;
      case SearchResultSorting.OLDEST:
        this.sortParams.ascending = true;
        this.sortParams.orderType = SortingOrderType.DATE;
        break;
      case SearchResultSorting.RELEVANCE:
        this.sortParams.ascending = false;
        this.sortParams.orderType = SortingOrderType.SCORE;
        break;

      default:
        return;
    }

    this.setSearchResults(this.searchParams, this.excludeIds, this.sortParams);
  }

  private setSearchResults(params: SearchParameters, excludeIds: string[], sortParams?: SortParams): void {
    this.resolver.state.params = params;
    this.resolver.state.sortParams = sortParams;
    this.resolver.state.excludeIds = excludeIds;

    this.searchService.getSearchResults({
      state: this.resolver.state,
      applyLocationAttrs: true,
    });

    this.isSearchActive = Boolean(params.queryText);
  }
}
